
import { Prop, Component, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { SchemeScope } from '@/components/mixins/schemescope';
import { Online } from '@/components/mixins/online';
import _isEmpty from 'lodash/isEmpty';
import MoreButton from '@/components/scheme/MoreButton.vue';
import draggable from 'vuedraggable';
import { LessonPlan } from '@/models/lessonPlan';
import AddLessonPlanForm from './AddLessonPlanForm.vue';

@Component({
  name: 'LessonPlanGroup',
  components: {
    MoreButton,
    draggable,
    AddLessonPlanForm,
  },
})
export default class LessonPlanGroup extends mixins(SchemeScope, Online) {
  private loading = true;
  private editing = false;
  private saving = false;
  private editingTitle = false;
  private newTitle = '';
  private search = '';
  private dirty = false;

  private groupPlans: Array<LessonPlan> = [];

  @Prop() lessonPlanGroupUuid!: string;

  @Watch('groupPlans')
  setDirty() {
    this.dirty = true;
  }

  async created() {
    this.loading = true;
    await this.$store.dispatch('planning/fetchItems', false);
    await this.$store.dispatch('lessonPlanGroups/fetchItems');
    this.$store.commit('planning/updateSchemeSlug', this.schemeSlug);
    this.$store.commit('lessonPlanGroups/updateSchemeSlug', this.schemeSlug);

    await this.$store.dispatch('lessonPlanGroup/fetchFromDb', this.lessonPlanGroupUuid);
    this.newTitle = this.group.title;

    this.$store.commit('planning/updateSearch', ''); // clear global search filter
    this.$store.commit('planning/updateItemsPerPage', 10000);
    await this.$store.dispatch('planning/fetchItems');

    this.groupPlans = this.group.plans;

    this.loading = false;

    await this.$nextTick(); // at nextTick the groupPlans changes, setting dirty=1, which we set back to false.
    this.dirty = false;
  }

  get lessonPlans() {
    const search = this.search === null ? '' : this.search.toLowerCase();

    // Exclude any plans already in this group, since they can only be added once.
    return this.$store.state.planning.items.filter(
      (plan: LessonPlan) =>
        !this.groupPlans.find((groupPlan: LessonPlan) => groupPlan.uuid === plan.uuid) &&
        (search.length === 0 || plan.title.toLowerCase().includes(search))
    );
  }

  get planningLink() {
    return { name: 'lesson-plans', params: { schemeSlug: this.schemeSlug } };
  }

  get group() {
    return this.$store.getters['lessonPlanGroup/selected'];
  }

  addToGroup(uuid: string) {
    const plan = this.$store.state.planning.items.find((plan: LessonPlan) => plan.uuid === uuid);
    this.groupPlans.push(plan);
  }

  removePlan(uuid: string) {
    const index = this.groupPlans.findIndex((groupPlan: LessonPlan) => groupPlan.uuid === uuid);
    if (index !== -1) {
      this.$delete(this.groupPlans, index);
    }
  }

  rename() {
    this.editingTitle = true;
  }

  async updateTitle() {
    this.saving = true;
    try {
      await this.$store.dispatch('lessonPlanGroup/updateTitle', this.newTitle);
      this.$alert('success', this.$t('planning.title_updated'));
    } catch (error) {
      this.$alert('error', this.$t('planning.title_not_updated'));
    }
    this.editingTitle = false;
    this.saving = false;
  }

  async saveGroupPlans() {
    this.saving = true;

    try {
      await this.$store.dispatch('lessonPlanGroup/updatePlans', {
        uuid: this.lessonPlanGroupUuid,
        plans: this.groupPlans,
      });
      this.dirty = false;
      this.$alert('success', this.$t('planning.plans_updated'));
    } catch (error) {
      this.$alert('error', this.$t('planning.plans_not_updated'));
    }

    this.saving = false;
  }

  cancelEditTitle() {
    this.editingTitle = false;
    this.newTitle = this.group.title;
  }

  get canEditGroup() {
    return this.$store.getters['auth/isFeatureEnabled']('CAN_EDIT_SESSION_PLAN_GROUP');
  }

  get canAddGroup() {
    return this.$store.getters['auth/isFeatureEnabled']('CAN_ADD_SESSION_PLAN_GROUP');
  }
}
